import { MessageModel } from "@/model/api/Message";
import { Options, Vue } from "vue-class-component";
import {Model, Prop} from "vue-property-decorator";
import {Shift} from "@/model/api/Shift";
import {buildBrokersMessage} from "./messageTemplates";

@Options({})
export default class MessageEditor extends Vue {
  @Prop({default: false})
  disabled: boolean;
  
  @Prop({default: "Message:"})
  messageLabel: string;

  @Prop({default: false})
  deleting: boolean;

  @Prop({default: false})
  showRecipientOptions: boolean;

  @Prop()
  readonly shift: Shift;

  @Model('message')
  messageModel: MessageModel;

  get disableSendAllBrokerDriver(): boolean {
    return process.env.VUE_APP_DISABLE_SEND_ALL_BROKER_DRIVER === 'true';
  }

  get hasAssignedDrivers(): boolean {
    return this.shift?.has_tickets === true;
  }

  get recipientType(): 'drivers_working' | 'brokers' | 'all' | null {
    if (this.messageModel?.is_only_drivers_working) {
      return 'drivers_working';
    } else if (this.messageModel?.is_brokers) {
      return 'brokers';
    }
    // "Send to all broker drivers" hidden: show no selection rather than a
    // radio that doesn't match what would actually be sent
    if (this.disableSendAllBrokerDriver) {
      return null;
    }
    return 'all';
  }

  set recipientType(value: 'drivers_working' | 'brokers' | 'all' | null) {
    if (value == null) {
      return;
    }
    let is_only_drivers_working = false;
    let is_brokers = false;
    let subject = this.messageModel.subject;
    let text = this.messageModel.text;

    switch (value) {
      case 'drivers_working':
        is_only_drivers_working = true;
        is_brokers = false;
        ({ subject, text } = this.restoreBrokersAutoFill(subject, text));
        break;
      case 'brokers': {
        is_only_drivers_working = false;
        is_brokers = true;
        // Logica per popolare subject e text quando si seleziona brokers
        const brokersMsg = buildBrokersMessage(this.shift, this.$config?.enableWeatherConditions === true);
        if (this.brokersAutoFill == null) {
          this.brokersAutoFill = {
            prevSubject: subject,
            prevText: text,
            genSubject: brokersMsg.subject,
            genText: brokersMsg.text
          };
        }
        subject = brokersMsg.subject;
        text = brokersMsg.text;
        break;
      }
      case 'all':
        is_only_drivers_working = false;
        is_brokers = false;
        ({ subject, text } = this.restoreBrokersAutoFill(subject, text));
        break;
    }

    this.messageModel = {
      ...this.messageModel,
      is_only_drivers_working,
      is_brokers,
      subject,
      text
    };
  }

  private brokersAutoFill: {
    prevSubject: string, prevText: string,
    genSubject: string, genText: string
  } | null = null;

  // leaving the brokers radio: drop the auto-generated recruiting template
  // (unless the operator edited it) and restore what was there before
  private restoreBrokersAutoFill(subject: string, text: string): { subject: string, text: string } {
    if (this.brokersAutoFill != null) {
      if (subject === this.brokersAutoFill.genSubject) subject = this.brokersAutoFill.prevSubject;
      if (text === this.brokersAutoFill.genText) text = this.brokersAutoFill.prevText;
      this.brokersAutoFill = null;
    }
    return { subject, text };
  }

  // default recipient when the dialog opens on a shift: "only drivers working"
  // when someone is assigned, the brokers recruiting message otherwise —
  // routed through the radio setter so display and model can never diverge
  applyDefaultRecipient() {
    if (!this.shift?.id) {
      return;
    }
    this.brokersAutoFill = null;
    this.recipientType = this.hasAssignedDrivers ? 'drivers_working' : 'brokers';
  }

  get subject() {
    return this.messageModel?.subject;
  }

  set subject(subject: string) {
    this.messageModel = {
      ...this.messageModel,
      subject
    };
  }

  get text() {
    return this.messageModel?.text;
  }

  set text(text: string) {
    this.messageModel = {
      ...this.messageModel,
      text
    };
  }

  get sendSms() {
    return this.messageModel?.is_sms;
  }

  set sendSms(is_sms: boolean) {
    this.messageModel = {
      ...this.messageModel,
      is_sms
    };
  }

  get sendEmail() {
    return this.messageModel?.is_email;
  }

  set sendEmail(is_email: boolean) {
    this.messageModel = {
      ...this.messageModel,
      is_email
    };
  }

  get sendPush() {
    return this.messageModel?.is_push;
  }

  set sendPush(is_push: boolean) {
    this.messageModel = {
      ...this.messageModel,
      is_push
    };
  }
}
